import {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react'
import {
  find,
} from 'lodash'
import axios from 'axios'

import applyLocalUpdatesFunc from '../Database.functions/applyLocalUpdatesFunc'
import useDbCollections from './useDbCollections'

const useDatabaseContext = () => {
  const {
    collections: db,
    updateCollections,
    loading: isLoading,
    refetch,
    setDocument,
    getDocument,
    getCollection,
  } = useDbCollections([
    'bankTransfers',
    'deckOrders',
    'transfersOrdersMatches',
    // 'dashboardSalesOrders', // TODO: Désactivé temporairement - base PowerBI (51.91.96.235) inaccessible. Voir PSQL_CONFIG.js
  ])

  const [frontBankTransfers, setfrontBankTransfers] = useState([])
  const [frontInvoices, setfrontInvoices] = useState([])
  const [selectedBankTransfer, setselectedBankTransfer] = useState()
  const [frontTransfersOrdersMatches, setfrontTransfersOrdersMatches] = useState([])
  const [dashboardSalesOrders, setDashboardSalesOrders] = useState([])

  // Stable applyLocalUpdates function
  const applyLocalUpdates = useCallback((axiosResponse) => {
    return applyLocalUpdatesFunc(axiosResponse, updateCollections)
  }, [updateCollections])

  // Process database collections when they change
  useEffect(() => {
    console.log('🔄 useDatabaseContext useEffect triggered', {
      dbKeys: Object.keys(db),
      isLoading,
      hasData: Object.keys(db).length > 0,
    })

    // Only process if we have actual data
    if (Object.keys(db).length === 0 && isLoading) {
      console.log('📦 Database still loading, keeping current state')
      return
    }

    const {
      bankTransfers: newbankTransfers = [],
      deckOrders: newinvoices = [],
      transfersOrdersMatches: newtransfersOrdersMatches = [],
      dashboardSalesOrders: newDashboardSalesOrders = [],
    } = db

    console.log('✅ Database collections processed:', {
      bankTransfers: newbankTransfers.length,
      deckOrders: newinvoices.length,
      transfersOrdersMatches: newtransfersOrdersMatches.length,
      dashboardSalesOrders: newDashboardSalesOrders.length,
      isLoading,
    })

    // 🔍 DEBUG - Diagnostic pour problème prod
    console.log('🔍 DEBUG - About to update states:', {
      newBankTransfersCount: newbankTransfers.length,
      newDeckOrdersCount: newinvoices.length,
      newTransfersOrdersMatchesCount: newtransfersOrdersMatches.length,
      dbKeysReceived: Object.keys(db),
      isLoading,
      timestamp: new Date().toISOString(),
    })
    
    // 🔍 DEBUG - Stack trace pour voir d'où vient l'appel
    if (newbankTransfers.length === 0) {
      console.log('⚠️ DEBUG - bankTransfers is EMPTY! Stack trace:')
      console.trace()
    }

    // Update all states in one batch
    setfrontBankTransfers(newbankTransfers)
    setfrontInvoices(newinvoices)
    setfrontTransfersOrdersMatches(newtransfersOrdersMatches)
    setDashboardSalesOrders(newDashboardSalesOrders)

    // Update selected bank transfer if it exists in new data
    setselectedBankTransfer(prevSelected => {
      if (!prevSelected?.id) return prevSelected
      return find(newbankTransfers, { id: prevSelected.id }) || prevSelected
    })
  }, [db, isLoading]) // Include isLoading to prevent processing during transitions

  // Compute matched invoices based on selected bank transfer
  const matchedInvoices = useMemo(() => {
    if (!selectedBankTransfer?.transferKey || !db.transfersOrdersMatches) {
      return []
    }

    console.log('🔍 COMPUTING matches for transferKey:', selectedBankTransfer.transferKey)

    const filteredMatches = db.transfersOrdersMatches
      .filter(({ transferKey = '' } = {}) => transferKey === selectedBankTransfer.transferKey)

    console.log('📋 Found', filteredMatches.length, 'matches')

    if (filteredMatches.length > 0) {
      const mappedInvoices = filteredMatches.map(({ orderKey = '' } = {}) => {
        return find(db.deckOrders, { id: orderKey })
      }).filter(Boolean)

      console.log('📄 Final matched invoices:', mappedInvoices.length)
      return mappedInvoices
    }

    return []
  }, [selectedBankTransfer?.transferKey, db.transfersOrdersMatches, db.deckOrders])

  // Stable callback for bank transfer selection
  const onSelectedBankTransferChange = useCallback((newSelection) => {
    setselectedBankTransfer(newSelection)
  }, [])

  // Memoize return value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    bankTransfers: frontBankTransfers,
    invoices: frontInvoices,
    selectedBankTransfer,
    matchedInvoices,
    onSelectedBankTransferChange,
    applyLocalUpdates,
    transfersOrdersMatches: frontTransfersOrdersMatches,
    dashboardSalesOrders,
    isLoading,
    refetch,
    getCollection,
    setDocument,
    getDocument,
  }), [
    frontBankTransfers,
    frontInvoices,
    selectedBankTransfer,
    matchedInvoices,
    onSelectedBankTransferChange,
    applyLocalUpdates,
    frontTransfersOrdersMatches,
    dashboardSalesOrders,
    isLoading,
    refetch,
    getCollection,
    setDocument,
    getDocument,
  ])

  return contextValue
}

export default useDatabaseContext
